<script lang="ts">
  // Die Statusleiste (docs/shell.md).
  //
  // Was hier steht, muss zwei Bedingungen erfuellen: es gilt IMMER (also
  // unabhaengig davon, welcher Tab offen ist), und man will es sehen,
  // ohne danach zu suchen. Alles andere gehoert auf eine Seite.
  //
  // Alle Werte kommen aus Stores, die ohnehin laufen -- die Leiste legt
  // keinen einzigen neuen Datenpfad an (docs/data-loading.md).
  //
  // BEWUSST unbunt (Tim 15.08.): die Leiste lief zuerst in Akzent-
  // Orange ueber die volle Breite und zog damit dauerhaft Blicke, ohne
  // dafuer etwas zu liefern. Sie traegt jetzt dieselbe ruhige Flaeche
  // wie das uebrige Chrome. Farbe bekommt nur noch, was WIRKLICH etwas
  // will: eine abgerissene Verbindung, haengende Aufgaben, Kritisches.

  import { onMount } from "svelte";

  import Icon from "../Icon.svelte";
  import { t } from "../i18n.svelte";
  import { getAlertsCount } from "../alerts-store.svelte";
  import { beendeBypass as bypassAus, getBypass } from "../bypass-summary.svelte";
  import { getCriticalSummary } from "../critical-summary.svelte";
  import { getTasksSummary } from "../tasks-summary.svelte";
  import LaeuftInhalt from "../LaeuftInhalt.svelte";
  import LaeuftListe from "../LaeuftListe.svelte";
  import { buendle, type Buendel } from "../laeuft";
  import { laeuftGerade, laeuftUhr } from "../laeuft.svelte";
  import { wsConnected } from "../ws-hub.svelte";
  import {
    buildZeit,
    getLadezeit,
    getVersionInfo,
    ms,
    startVersionInfo,
  } from "../version-info.svelte";
  import { panelOffen, schaltePanel, zeigeReiter } from "./chrome.svelte";
  import { oeffnePfad } from "./route-sync.svelte";
  import { getTabLadezeit } from "./tab-timing.svelte";

  const verbunden = $derived(wsConnected());
  const panel = $derived(panelOffen());
  const meldungen = $derived(getAlertsCount());
  // Der Bypass haengt die Anmeldung aus. Solange er laeuft, MUSS man das
  // sehen -- egal auf welcher Seite man gerade ist.
  //
  // Er hatte diesen Platz schon einmal: das Schloss sass in der TopBar,
  // und die rendert seit dem Shell-Umbau nur noch mobil. Auf dem Desktop
  // gab es dadurch keinen Hinweis mehr (Tim 18.08.). Die Leiste ist der
  // richtige Ort: sie gilt immer, und Farbe bekommt hier nur, was
  // wirklich etwas will.
  let bypassOffen = $state(false);
  let bypassLaeuft = $state(false);

  async function beendeBypass(): Promise<void> {
    if (bypassLaeuft) return;
    bypassLaeuft = true;
    try {
      // Gescheitert: bypassAus meldet den Grund, das Fenster bleibt offen.
      if (await bypassAus()) bypassOffen = false;
    } finally {
      bypassLaeuft = false;
    }
  }

  const bypass = $derived(getBypass());
  const bypassAktiv = $derived(!!bypass?.active);
  const bypassRest = $derived.by(() => {
    const s = bypass?.remainingS ?? 0;
    if (!bypass || bypass.autoOffMin <= 0 || s <= 0) return "";
    const std = Math.floor(s / 3600);
    const min = Math.floor((s % 3600) / 60);
    return std > 0 ? `${std} h ${min} min` : `${min} min`;
  });
  const kritisch = $derived(getCriticalSummary()?.count ?? 0);
  const tasks = $derived(getTasksSummary());
  const tasksLaufen = $derived(tasks?.running_now ?? 0);
  // Läuft gerade (lib/laeuft.svelte.ts): ein Chip je Gerät, das arbeitet --
  // der Klick springt hin (Tim 15.10.2026). Gilt unabhängig vom offenen
  // Tab, gehört also hierher.
  // Gleichartiges steht als EIN Eintrag da („Claude 2 arbeiten"), der
  // Klick zeigt dann die Liste (LaeuftListe).
  const laeuft = $derived(buendle(laeuftGerade()));
  const uhr = laeuftUhr(() => laeuft.length > 0);
  let listeAuf = $state<{ id: string; anker: HTMLElement } | null>(null);
  const liste = $derived(listeAuf ? laeuft.find((b) => b.id === listeAuf!.id) : undefined);
  function laeuftKlick(b: Buendel, e: MouseEvent): void {
    if (b.eintraege.length === 1) oeffnePfad(b.eintraege[0].pfad);
    else listeAuf = listeAuf?.id === b.id ? null : { id: b.id, anker: e.currentTarget as HTMLElement };
  }

  // Version und Ladezeit standen im Seiten-Fuss, den es in der Shell
  // nicht mehr gibt (Tim 15.08.: "sollte kompakt wieder zu finden
  // sein"). Kompakt heisst hier: die kurze Fassung steht da, die
  // Aufschluesselung liegt im Tooltip -- eine Leiste ist zum
  // Vorbeischauen da, nicht zum Lesen.
  onMount(startVersionInfo);
  const version = $derived(getVersionInfo());
  const ladezeit = $derived(getLadezeit());
  const versionTip = $derived(
    version
      ? [
          version.commit ? `Commit ${version.commit}` : "",
          `Build ${buildZeit(version.build_time)}`,
          version.go_version ? `Go ${version.go_version}` : "",
        ]
          .filter(Boolean)
          .join(" · ")
      : "",
  );
  const ladeTip = $derived(
    ladezeit
      ? [
          t("footer.load.first", "Erster Seitenaufruf"),
          `${t("footer.load.backend", "Backend")} ${ms(ladezeit.server)}`,
          `${t("footer.load.transfer", "Übertragung")} ${ms(ladezeit.transfer)}`,
          `${t("footer.load.render", "Aufbau im Browser")} ${ms(ladezeit.build)}`,
        ].join(" · ")
      : "",
  );

  // Ladezeit des zuletzt geoeffneten Tabs. Anders als die Zahl daneben
  // ist die hier lebendig: jeder Tab-Wechsel baut den Inhalt neu auf.
  const tabzeit = $derived(getTabLadezeit());
  const tabTip = $derived(
    tabzeit
      ? [
          tabzeit.titel,
          `${t("shell.tabload.build", "Aufbau")} ${ms(tabzeit.aufbau)}`,
          `${t("shell.tabload.data", "Daten")} ${ms(tabzeit.daten)}`,
          tabzeit.daten === 0
            ? t("shell.tabload.cached", "stand sofort komplett da")
            : "",
        ]
          .filter(Boolean)
          .join(" · ")
      : "",
  );
</script>

<footer class="statusbar text-fg-muted flex items-center gap-0.5">
  <!-- Die Verbindung ist das Wichtigste: steht sie nicht, sind ALLE
       anderen Zahlen auf dieser Oberflaeche womoeglich veraltet. -->
  <!-- Der Stecker sagt es ohne Wort: steckt er, laeuft es (gruen);
       ist er gezogen, stimmt etwas nicht (rot). Beschriftet wird nur
       der Warnfall -- "verbunden" ist der Normalzustand und braucht
       keine Worte, "getrennt" schon. -->
  <span
    class="si block"
    class:verbunden
    class:getrennt={!verbunden}
    title={verbunden
      ? t("shell.ws_ok", "Mit dem Server verbunden — Werte kommen live")
      : t("shell.ws_lost", "Keine Verbindung — Werte können veraltet sein")}
  >
    <!-- Der Block traegt die MARKE, nicht den Verbindungszustand: das
         Wort "Verbunden" sagte nur, was ohnehin der Normalfall ist.
         Den Zustand tragen Stecker und Flaeche -- gruener Stecker auf
         nichts, oder alles rot. Damit wandert "homelab" von ganz oben
         hierher und die obere Leiste konnte weg (Tim 18.08.). -->
    <Icon name={verbunden ? "plug" : "plug-off"} size={17} />
    {verbunden ? t("shell.marke", "homelab") : t("shell.disconnected", "Getrennt")}
  </span>

  <!-- Direkt nach der Verbindung: beides sind Zustaende der Anlage,
       keine Zaehler. Der Klick fuehrt dorthin, wo man ihn abschaltet. -->
  {#if bypassAktiv}
    <div class="bypass-halter">
      {#if bypassOffen}
        <!-- Direkt hier beenden, nicht erst auf einer Seite: ein Loch zu
             schliessen darf nie mehr als einen Klick kosten (Tim 18.08.).
             Der Weg in die Einstellungen bleibt daneben, fuer alles
             andere. -->
        <div class="bypass-blase">
          <p class="titel">{t("shell.bypass_titel", "Anmeldung ist ausgehebelt")}</p>
          <p class="text">
            {t(
              "shell.bypass_text",
              "Anfragen vom Server selbst kommen ohne Anmeldung durch.",
            )}{bypassRest ? " " + t("shell.bypass_rest", "Endet von selbst in {z}.").replace("{z}", bypassRest) : ""}
          </p>
          <div class="knoepfe">
            <button
              type="button"
              class="jetzt"
              disabled={bypassLaeuft}
              onclick={beendeBypass}
            >
              {t("shell.bypass_beenden", "Jetzt beenden")}
            </button>
            <button
              type="button"
              class="mehr"
              onclick={() => {
                bypassOffen = false;
                oeffnePfad("/einstellungen");
              }}
            >
              {t("shell.bypass_mehr", "Einstellungen")}
            </button>
          </div>
        </div>
      {/if}
    <button
      type="button"
      class="si stark"
      aria-expanded={bypassOffen}
      onclick={() => (bypassOffen = !bypassOffen)}
      title={t(
        "shell.bypass_hint",
        "Der Bypass hebt die Anmeldung für lokale Zugriffe aus — klicken zum Beenden",
      )}
    >
      <!-- Ohne Beschriftung: das offene Schloss in Rot sagt es, die
           Restzeit gibt ihm Dringlichkeit, und was genau offen steht,
           steht im Tooltip. Der Satz daneben machte die Leiste breit,
           ohne mehr zu sagen (Tim 18.08.). -->
      <Icon name="lock-open" size={15} />
      {#if bypassRest}<span class="num">{bypassRest}</span>{/if}
    </button>
    </div>
  {/if}

  <button
    type="button"
    class="si"
    class:laeuft={tasksLaufen > 0}
    onclick={() => oeffnePfad("/system/tasks")}
    title={tasksLaufen > 0
      ? t("shell.tasks_running", "{n} Aufgaben laufen gerade").replace("{n}", String(tasksLaufen))
      : t("shell.tasks_idle", "Gerade läuft keine Aufgabe")}
  >
    <!-- Nur das Symbol, keine Zahl (Tim 18.08.). Die Aufgabenliste
         pulst leise, solange etwas laeuft, und steht still, wenn Ruhe
         ist -- das ist die ganze Aussage. Der PROBLEM-Fall gehoert
         hier nicht hin: eine fehlgeschlagene Aufgabe erzeugt bereits
         eine Meldung ("Task fehlgeschlagen", alerts-store.svelte.ts)
         und zaehlt damit im Achtungssymbol rechts. Zweimal dieselbe
         Sache waere zweimal zu pflegen. -->
    <Icon name="checks" size={15} />
  </button>

  {#each laeuft as b (b.id)}
    <button
      type="button"
      class="si lauf {b.ton}"
      onclick={(e) => laeuftKlick(b, e)}
      title={b.titel}
      aria-haspopup={b.eintraege.length > 1 ? "menu" : undefined}
      aria-expanded={b.eintraege.length > 1 ? listeAuf?.id === b.id : undefined}
    >
      <span class="lauf-punkt" aria-hidden="true"></span>
      <span class="num"><LaeuftInhalt {b} jetzt={uhr.jetzt} /></span>
    </button>
  {/each}
  {#if liste && liste.eintraege.length > 1 && listeAuf}
    <LaeuftListe eintraege={liste.eintraege} anker={listeAuf.anker} onwahl={oeffnePfad} onzu={() => (listeAuf = null)} />
  {/if}

  <div class="flex-1"></div>

  {#if version}
    <span class="si num" title={versionTip}>{version.version || version.commit}</span>
  {/if}
  {#if ladezeit}
    <span class="si num" title={ladeTip}>{ms(ladezeit.total)}</span>
  {/if}
  <!-- Die Tab-Zeit steht NEBEN der des ersten Seitenaufrufs, nicht
       statt ihr: die eine sagt, wie teuer der Einstieg war, die andere,
       wie teuer das Arbeiten ist. Das Vorzeichen unterscheidet sie --
       zwei nackte Zahlen nebeneinander waeren nicht zuzuordnen. -->
  {#if tabzeit}
    <span class="si num" title={tabTip}>
      {t("shell.tabload.label", "Tab")}
      {tabzeit.gedeckelt ? "≥" : ""}{ms(tabzeit.gesamt)}
    </span>
  {/if}

  <!-- Der Panel-Schalter kommt aus der weggefallenen oberen Leiste.
       Hier passt er besser als dort: das Panel sitzt direkt darueber. -->
  <button
    type="button"
    class="si"
    class:an={panel}
    aria-pressed={panel}
    title={t("shell.toggle_panel", "Unteres Panel (Strg J)")}
    aria-label={t("shell.toggle_panel", "Unteres Panel (Strg J)")}
    onclick={() => schaltePanel()}
  >
    <!-- Der Pfeil zeigt, WOHIN es geht, nicht wo das Panel ist: zu ist
         es, also faehrt es nach oben aus; offen faehrt es nach unten
         weg (Tim 18.08.). -->
    <Icon name={panel ? "chevron-down" : "chevron-up"} size={15} />
  </button>

  {#if kritisch > 0}
    <button
      type="button"
      class="si stark"
      onclick={() => zeigeReiter("meldungen")}
      title={t("shell.critical_hint", "Kritische Ereignisse zeigen")}
    >
      <Icon name="alert-octa" size={15} />
      <span class="num">{kritisch}</span>
    </button>
  {/if}

  <!-- Der Zaehler oeffnet das untere Panel, nicht die Seite: wer beim
       Arbeiten auf eine Meldung schaut, will nicht den Tab verlieren,
       an dem er gerade sitzt. Die Vollansicht steht im Panel selbst. -->
  <button
    type="button"
    class="si"
    onclick={() => zeigeReiter("meldungen")}
    title={t("shell.alerts_hint", "Meldungen im unteren Panel zeigen")}
  >
    <Icon name="alert-tri" size={15} />
    <span class="num">{meldungen}</span>
  </button>
</footer>

<style>
  .statusbar {
    height: 1.6rem;
    flex: none;
    padding: 0 0.35rem;
    font-size: 0.8rem;
  }
  .si {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0 0.45rem;
    height: 1.6rem;
    border-radius: 3px;
    white-space: nowrap;
  }
  button.si:hover {
    background: var(--bg-elev);
    color: var(--fg);
  }
  /* Der Verbindungs-Block. Form wie die Remote-Anzeige in VS Code --
     eine gefuellte Flaeche ganz links -- aber nicht deren Lautstaerke.
     Der Grundsatz dieser Leiste ist "bewusst unbunt": Farbe bekommt
     nur, was WIRKLICH etwas will (Tim 15.08.). Verbunden ist der
     Normalfall und will nichts, also traegt der Block dort eine ruhige
     Flaeche und nur der Stecker etwas Gruen. Reisst die Leitung ab,
     wird derselbe Block ganz rot -- dann will er etwas (Tim 18.08.). */
  .block {
    /* Nicht randlos und nicht kantig: der Block sitzt frei in der
       Leiste statt in ihrer Ecke zu kleben. Deshalb niedriger als die
       Leiste (die Flex-Zeile zentriert ihn dann von selbst), ein
       Stueck Luft nach links und weiche Ecken (Tim 18.08.). */
    height: 1.25rem;
    margin: 0 0.4rem 0 0.15rem;
    padding: 0 0.55rem;
    border-radius: 5px;
    font-weight: 600;
  }
  /* Verbunden traegt GAR KEINE Flaeche (Tim 18.08.): der Normalfall
     soll die Leiste nicht mit einem Kasten belegen. Die Masse
     (Hoehe, Rundung, Abstand) bleibt trotzdem stehen -- so springt
     nichts, wenn der rote Kasten kommt. */
  .block.verbunden {
    background: transparent;
    color: var(--fg-muted);
  }
  /* Der Stecker ist das einzige Zeichen für „alles kommt live" -- er darf
     nicht dünner dastehen als das Wort daneben (Tim 04.10.2026: „könnte
     noch was kräftiger sein"). Darum größer, mit dickerem Strich und
     gefülltem Körper statt als Haarlinie. */
  .block.verbunden :global(svg) {
    color: var(--success);
    stroke-width: 2;
    fill: color-mix(in oklab, currentColor 32%, transparent);
  }
  .block.getrennt {
    background: var(--danger);
    color: #fff;
  }
  /* Im Dunkeln ist --danger deutlich heller (70 % statt 58 %) -- dort
     traegt nur dunkle Schrift genug Kontrast. */
  :global(html.dark) .block.getrennt {
    color: #0e1116;
  }

  /* Leises Pulsen, solange etwas laeuft. Bewusst langsam und ohne
     Farbe: es soll am Rand des Blickfelds bemerkbar sein, nicht
     ziehen. Wer Bewegung abgestellt hat, bekommt keine. */
  .si.laeuft :global(svg) {
    animation: laeuft-puls 1.8s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .si.laeuft :global(svg) {
      animation: none;
      opacity: 1;
    }
  }
  /* Läuft gerade: der Punkt trägt den Zustand (Akzent = läuft, gelb =
     wartet auf einen Handgriff), die Schrift bleibt ruhig. Gepulst wird
     nur, was läuft -- und nicht bei „Bewegung reduzieren". */
  .si.lauf {
    color: var(--fg);
  }
  .lauf-punkt {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    flex: none;
    background: var(--fg-subtle);
  }
  .si.lauf.laeuft .lauf-punkt {
    background: var(--color-laeuft);
    animation: laeuft-puls 1.8s ease-in-out infinite;
  }
  .si.lauf.warn .lauf-punkt {
    background: var(--warning);
  }
  .si.lauf.warn {
    color: var(--warning);
  }
  @media (prefers-reduced-motion: reduce) {
    .si.lauf.laeuft .lauf-punkt {
      animation: none;
    }
  }
  button.si.an {
    color: var(--fg);
    background: var(--bg-elev);
  }
  .si.stark {
    color: var(--danger);
    font-weight: 600;
  }
  /* Die Blase haengt UEBER der Leiste, nicht darunter -- unter ihr ist
     der Bildschirmrand. */
  .bypass-halter {
    position: relative;
    display: flex;
  }
  .bypass-blase {
    position: absolute;
    bottom: calc(100% + 0.35rem);
    left: 0;
    z-index: 60;
    width: 17rem;
    padding: 0.6rem 0.7rem;
    border: 1px solid var(--danger);
    border-radius: 0.5rem;
    background: var(--bg-elev);
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.25);
    text-align: left;
    white-space: normal;
  }
  .bypass-blase .titel {
    color: var(--danger);
    font-weight: 600;
    font-size: 0.8rem;
  }
  .bypass-blase .text {
    color: var(--fg-muted);
    font-size: 0.75rem;
    line-height: 1.35;
    margin-top: 0.15rem;
  }
  .bypass-blase .knoepfe {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.5rem;
  }
  .bypass-blase .jetzt {
    background: var(--danger);
    color: #fff;
    font-weight: 600;
    font-size: 0.75rem;
    padding: 0.25rem 0.6rem;
    border-radius: 0.4rem;
  }
  .bypass-blase .mehr {
    border: 1px solid var(--border);
    color: var(--fg-muted);
    font-size: 0.75rem;
    padding: 0.25rem 0.6rem;
    border-radius: 0.4rem;
  }
  .num {
    font-variant-numeric: tabular-nums;
  }
  /* Rechnerisch sitzen die Symbole exakt mittig (gemessen: Mitte 888 in
     einer Leiste mit Mitte 888). Sie WIRKEN trotzdem tief, weil die
     sichtbare Masse von Schrift oberhalb der Boxmitte liegt -- die
     Oberlaengen zaehlen mit, die Unterlaengen kaum. Ein halbes Pixel
     hoch gleicht das aus, ohne dass etwas verrutscht (Tim 15.08.). */
  .si :global(svg) {
    transform: translateY(-0.5px);
  }
</style>
